import { useEffect, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { AuditLogEntry } from '../types';

interface AuditLogTabProps {
  auditLog: AuditLogEntry[];
  loading: boolean;
  onLoad: () => Promise<void>;
}

export function AuditLogTab({ auditLog, loading, onLoad }: AuditLogTabProps) {
  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const columns = useMemo<ColumnDef<AuditLogEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'user_id',
        header: 'User',
        cell: ({ getValue }) => {
          const val = getValue() as string | null | undefined;
          return (
            <span className="font-mono text-[12px] text-[#666]">
              {val ? `${val.slice(0, 8)}...` : '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ getValue }) => (
          <span className="font-medium text-[#1a1a1a]">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Timestamp',
        cell: ({ getValue }) => {
          const val = getValue() as string;
          return val
            ? new Date(val).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—';
        },
      },
      {
        accessorKey: 'details',
        header: 'Details',
        cell: ({ getValue }) => <span className="text-[#666]">{getValue() as string}</span>,
      },
    ],
    [],
  );

  if (loading) {
    return <LoadingSpinner size="md" text="Loading audit log..." />;
  }

  if (auditLog.length === 0) {
    return (
      <div className="rounded-xl border border-[#eee] bg-white p-6 text-center text-[#999]">
        No audit log entries
      </div>
    );
  }

  // Sorted by timestamp descending (newest first)
  const sorted = [...auditLog].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  return <DataTable columns={columns} data={sorted} />;
}
